import { get } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import type { AllowedIdField, BasicRecord, SCAdvanceTableProps } from '../table';
import { SCAdvanceTable } from '../table';
import { SCTableSelected } from '../table_selected';
import type { SCFormFieldProps } from './form_field';
import { SCFormField } from './form_field';

interface SCFormFieldSCTableProps<D extends BasicRecord>
  extends SCFormFieldProps, Omit<SCAdvanceTableProps<D>, 'Rows'> {
  Rows?: SCAdvanceTableProps<D>['Rows'];
  ChangeValueTransform?: (params: { id: string; field: keyof D; value: D[]; preValue: D[] }) => D[];
  Filter?: (row: D) => boolean;
  ShowSelected?: {
    title?: string;
    displayName: (row: D) => string;
    isSelect: (row: D) => boolean;
    OnDelete?: (id: string) => void;
  };
}
type ChangeValueFn<D extends BasicRecord> = NonNullable<SCAdvanceTableProps<D>['ChangeValue']>;

// safe immutable empty arr
const emptyArr = Object.freeze([]);

function WithValueOnChange<D extends BasicRecord>({
  value,
  onChange,
  IdField,
  Rows,
  ChangeValueTransform,
  children,
}: {
  value: D[];
  onChange: (newValue: D[]) => void;
  Rows: SCAdvanceTableProps<D>['Rows'];
  IdField: AllowedIdField<D>;
  ChangeValueTransform?: SCFormFieldSCTableProps<D>['ChangeValueTransform'];
  children: (props: {
    Rows: NonNullable<SCAdvanceTableProps<D>['Rows']>;
    ChangeValue: ChangeValueFn<D>;
  }) => React.ReactNode;
}) {
  const newRows = useMemo(() => {
    if (!Rows) {
      return value || emptyArr;
    }
    return Rows.map((row, index) => {
      return {
        ...row,
        ...(value?.[index] || {}),
      };
    });
  }, [Rows, value]);

  const handleChangeValue = useCallback<ChangeValueFn<D>>(
    (id, field, v) => {
      const newValue = newRows.map((item, index): D => {
        // FIXME: defense for item not found
        const vItem = value ? value[index] : ({} as D);
        if (item[IdField] === id) {
          return {
            ...vItem,
            [field]: v,
            [IdField]: id,
          };
        }
        return vItem;
      });
      if (ChangeValueTransform) {
        onChange(
          ChangeValueTransform({
            id,
            field,
            value: newValue,
            preValue: value,
          }),
        );
      } else {
        onChange(newValue);
      }
    },
    [newRows, ChangeValueTransform, value, IdField, onChange],
  );

  return children({ Rows: newRows, ChangeValue: handleChangeValue });
}

function SCFormFieldSCTable<D extends BasicRecord>({
  label,
  name,
  required,
  disabled,
  rules,
  ChangeValueTransform,
  ShowSelected,
  Filter,
  TableExtraProps,
  ...rest
}: SCFormFieldSCTableProps<D>) {
  return (
    <SCFormField
      label={label}
      name={name}
      disabled={disabled}
      required={required}
      rules={rules}
      withMarginBottom
      // Note: disable validation-error marker specificly for the case of Table as a form-field, otherwise the SearchBar of the Table will be marked unexpectedly
      disableFormFieldError
      disableSelfStyleOfDisabled
      MaxHeightWithBreadcrumb
    >
      {({ field, fieldState }) => {
        return (
          <WithValueOnChange
            IdField={rest.IdField}
            Rows={rest.Rows}
            value={field.value}
            onChange={field.onChange}
            ChangeValueTransform={ChangeValueTransform}
          >
            {({ Rows, ChangeValue }) => {
              return (
                <>
                  {ShowSelected && (
                    <SCTableSelected
                      title={ShowSelected.title}
                      displayName={ShowSelected.displayName}
                      disabled={field.disabled}
                      Rows={Rows.filter((row) => ShowSelected.isSelect(row))}
                      IdField={rest.IdField}
                      OnDelete={ShowSelected.OnDelete}
                    />
                  )}
                  <SCAdvanceTable
                    {...rest}
                    TableExtraProps={
                      TableExtraProps ?? {
                        RowExtraProps(_, index) {
                          const hasError = get(fieldState?.error, index);
                          return {
                            Class: hasError ? 'table-row-has-error' : undefined,
                          };
                        },
                      }
                    }
                    ReadOnly={field.disabled}
                    Rows={Filter ? Rows.filter(Filter) : Rows}
                    ChangeValue={ChangeValue}
                  />
                </>
              );
            }}
          </WithValueOnChange>
        );
      }}
    </SCFormField>
  );
}

export { SCFormFieldSCTable };
export type { SCFormFieldSCTableProps };
